/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo } from 'react';
import type {
  ComponentType,
  MouseEventHandler,
  ReactElement,
  ReactNode,
} from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { definition as faEllipsisH } from '@fortawesome/free-solid-svg-icons/faEllipsisH';

import {
  Button,
  buttonColors,
} from '../Button';
import type {
  ButtonProps,
  ButtonColor,
} from '../Button';
import { ButtonGroup } from '../ButtonGroup';
import { Dropdown } from '../Dropdown';
import type {
  DropdownBase,
} from '../Dropdown';

const dropdownComponentProps = {
  type: 'button' as const,
};

export type ButtonWithDropdownProps<
ButtonComponentType extends ComponentType<any> | keyof JSX.IntrinsicElements = 'button',
> = Omit<ButtonProps<ButtonComponentType>, 'color' | 'borderCollapse'> & {
  /**
   * props компонента Dropdown
   */
  dropdown: DropdownBase;

  /**
   * Обработчик нажатия на кнопку
   */
  onClick?: MouseEventHandler;
  /**
   * Выключены ли кнопка и дропдаун
   */
  disabled?: boolean;
  /**
   * Выключена ли кнопка
   */
  buttonDisabled?: boolean;
  /**
   * Находится ли кнопка в состоянии загрузки
   */
  loading?: boolean;
  /**
   * Цвет
   */
  color?: ButtonColor;
  /**
   * Выключена ли кнопка открытия выпадающего списка
   */
  dropdownDisabled?: boolean;

  /**
   * Текст кнопки
   */
  children?: ReactNode;
};

function ButtonWithDropdownInner<
ButtonComponentType extends ComponentType<any> | keyof JSX.IntrinsicElements = 'button',
>({
  dropdown,

  onClick: onClickProp = undefined,
  disabled = false,
  buttonDisabled = false,
  loading = false,
  color = buttonColors.DEFAULT,
  component = undefined,
  componentProps = undefined,

  dropdownDisabled = false,

  children = undefined,

  ...rest
}: ButtonWithDropdownProps<ButtonComponentType>): ReactElement {
  return (
    <Dropdown
      placement="bottom-start"
      {...dropdown}
    >
      {({
        isDropdownOpen,
        onClick: onClickRenderProp,
        ref,
      }): ReactNode => (
        <ButtonGroup
          {...rest}
          color={color}
          borderCollapse={color === buttonColors.DEFAULT && !disabled}
          ref={ref}
        >
          <Button
            onClick={onClickProp}
            disabled={disabled || buttonDisabled}
            loading={loading}
            component={component}
            componentProps={componentProps}
          >
            {children}
          </Button>

          <Button
            onlyIcon
            componentProps={dropdownComponentProps}
            disabled={disabled || dropdownDisabled}
            onClick={onClickRenderProp}
            pressed={isDropdownOpen}
          >
            <FontAwesomeIcon
              icon={faEllipsisH}
            />
          </Button>
        </ButtonGroup>
      )}
    </Dropdown>
  );
}

export const ButtonWithDropdown = memo(ButtonWithDropdownInner) as typeof ButtonWithDropdownInner;
